import { Color, colorArrayToRgb, ColorRange, coolPurple6, rgbaToRgb } from '@methanesat/colors';
import { alpha, Theme } from '@methanesat/ui-components';

import { DISPERSED_SOURCE_PALETTE, genericMapBackground, scaleData, ScaleDataKeys } from '../consts';
import { AreaEmissionsProduct, AreaEmissionsProducts, Platforms, RASTER_COLORMAP_NAMES } from '../types';

/**
 * Given a rgba color, it applies the map background to it
 * and returns a rgb color. Given a rgb color, it returns
 * the same rgb color.
 * @example
 * colorWithMapBackground([50, 245, 101, 127])
 * // returns [131, 228, 154]
 */
export const colorWithMapBackground = (color: Color) => {
    if (color.length === 3) {
        return color;
    }

    return rgbaToRgb(color, genericMapBackground);
};

/**
 * Returns the array of colors used for area emissions
 */
export const getColorRangeColors = (
    theme: Theme,
    enabled: boolean,
    product: AreaEmissionsProduct = AreaEmissionsProducts.l4,
    platform: Platforms = Platforms.MSAT,
    scaleName?: ScaleDataKeys
) => {
    // default to L3 colors
    let colors: ColorRange = coolPurple6;
    if (product === AreaEmissionsProducts.l4 && !scaleName) {
        colors = DISPERSED_SOURCE_PALETTE[platform];
    } else if (product === AreaEmissionsProducts.l4 && scaleName) {
        if (scaleName in scaleData && scaleData[scaleName]) {
            colors = scaleData[scaleName].colorRange;
        } else {
            // L4 default is msatWarmDefault
            colors = scaleData[RASTER_COLORMAP_NAMES.msatWarmDefault].colorRange;
        }
    }

    return colors.map((color) => {
        const rgbColor = colorArrayToRgb(colorWithMapBackground(color));

        // When disabled, the colors become paler, which is why the alpha function is used
        return enabled ? rgbColor : alpha(rgbColor, theme.palette.action.disabledOpacity);
    });
};

/**
 * Returns the array of colors used for area emissions icons
 */
export const getAreaEmissionsIconColors = (
    theme: Theme,
    enabled: boolean,
    product: AreaEmissionsProducts,
    platform: Platforms
) => {
    let colors: ColorRange = DISPERSED_SOURCE_PALETTE[platform];

    if (product === AreaEmissionsProducts.l3) {
        colors = coolPurple6;
    }

    return colors.map((color) => {
        const rgbColor = colorArrayToRgb(colorWithMapBackground(color));

        // When disabled, the colors become paler, which is why the alpha function is used
        return enabled ? rgbColor : alpha(rgbColor, theme.palette.action.disabledOpacity);
    });
};

export const calculateDispersedIconColors = (
    theme: Theme,
    areaEmissionLayerEnabled: boolean,
    product: AreaEmissionsProducts,
    platform: Platforms
) => {
    const colors = getAreaEmissionsIconColors(theme, areaEmissionLayerEnabled, product, platform);
    // this allows us to pick which colors go in icon to meet different requirements between MAIR and MSAT
    const colorIndices = [platform === Platforms.MSAT ? 3 : 2, 4, 5, 7, 9, 13];
    return colorIndices.map((v) => colors[v]);
};

interface UnanchoredScaleProps {
    min: number;
    max: number;
    value: number;
}
interface AnchoredScaleProps {
    value: number;
    anchors: number[];
    // whole numbers, 0-100
    percentages: number[];
}

/**
 *
 */
export const getLogPositionOnScale = ({ min, max, value }: UnanchoredScaleProps) => {
    if (value < min) return 0;
    if (value > max) return 100;
    return Math.log10(((value - min) * 9) / (max - min) + 1) * 100;
};

export const getLinearPositionOnScale = ({ min, max, value }: UnanchoredScaleProps) => {
    const range = max - min;
    const position = Math.min(((value - min) / range) * 100, 100);
    return position > 0 ? position : 0;
};

export const getLinearPositionBetweenAnchorsOnScale = ({ value, anchors, percentages }: AnchoredScaleProps) => {
    // for value before range it positions arrow at the start
    if (value <= anchors[0]) {
        return percentages[0];
    }

    // position arrow for non-linear color scale:
    //   * find the anchor range the value falls within
    //   * position the arrow within that range as though it's a linear sub-section
    for (let i = 0; i < anchors.length - 1; i++) {
        const anchorStart = anchors[i];
        const anchorEnd = anchors[i + 1];
        const percentageStart = percentages[i];
        const percentageEnd = percentages[i + 1];

        // check if value is within the current anchor range
        if (value >= anchorStart && value <= anchorEnd) {
            const range = anchorEnd - anchorStart;
            const percentageRange = percentageEnd - percentageStart;

            // find percentage position within the range
            const position = percentageStart + ((value - anchorStart) / range) * percentageRange;

            return position;
        }
    }
    return percentages[percentages.length - 1];
};

// Retrieves the colormap name to be used for COG requests
export const getRasterColorMapName = (
    platform: Platforms,
    product: AreaEmissionsProducts,
    l4MSATColorScale: RASTER_COLORMAP_NAMES,
    dimmed?: boolean
): RASTER_COLORMAP_NAMES => {
    // L3 always uses cool purple color map
    if (product === AreaEmissionsProducts.l3) return RASTER_COLORMAP_NAMES.msatCoolLog;

    // L4 MAIR uses warm linear
    if (platform === Platforms.MAIR) return RASTER_COLORMAP_NAMES.mairWarmLinear;

    return dimmed ? RASTER_COLORMAP_NAMES.msatDimState : l4MSATColorScale;
};
